import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { AutoplayVariation } from '@/store/videoFeatures/types/autoplay-ab-test';
import { AutoplayPreviewMini } from '../AutoplayPreviewMini';
import { cn } from '@/lib/utils';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface AutoplayABOrderCardProps {
  variation: AutoplayVariation;
  orderIndex: number;
  isPrimary: boolean;
}

export function AutoplayABOrderCard({ variation, orderIndex, isPrimary }: AutoplayABOrderCardProps) {
  useLanguage();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: variation.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "flex items-center gap-3 p-3 bg-card border border-border rounded-xl",
        "hover:shadow-md transition-all duration-200",
        isDragging && "opacity-50 shadow-lg border-primary"
      )}
    >

      {/* Mini Preview */}
      <div className="relative w-14 h-9 bg-slate-900 rounded-lg overflow-hidden shrink-0">
        <div className="absolute inset-0 flex items-center justify-center p-1">
          <AutoplayPreviewMini 
            colors={variation.customization.colors}
            textTop={variation.customization.textTop}
            textBottom={variation.customization.textBottom}
            className="transform scale-[0.35]"
          />
        </div>
      </div>

      {/* Name + Status */}
      <div className="flex-1 min-w-0">
        <Tooltip>
          <TooltipTrigger asChild>
            <h4 className="font-medium text-sm text-foreground line-clamp-2 leading-tight cursor-default">
              {variation.name}
            </h4>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-[240px]">
            <p className="font-medium">{variation.name}</p>
          </TooltipContent>
        </Tooltip>
      </div>

      {/* Status Badge */}
      <div className="shrink-0">
        {isPrimary ? (
          <Badge className="bg-primary/10 text-primary border-primary/30 text-[10px] px-1.5 py-0">
            {translateInterfaceText("Principal")}</Badge>
        ) : (
          <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-muted-foreground">
            {translateInterfaceText("Em teste")}</Badge>
        )}
      </div>
    </div>
  );
}
